import { useState, useEffect } from 'react';
import { Minus, Plus } from 'lucide-react';

interface NumberInputProps {
  value: number | undefined;
  onChange: (val: number | undefined) => void;
  min: number;
  max: number;
  step?: number;
  placeholder?: string;
  className?: string;
  /** Decimal places to round to when committing (default 0 = integers) */
  decimalPlaces?: number;
}

/**
 * Mobile-friendly number input: renders a text field with
 * an explicit – / + stepper on each side.  On mobile it
 * opens a numeric keyboard (no spinner arrows).
 */
export default function NumberInput({
  value,
  onChange,
  min,
  max,
  step = 1,
  placeholder,
  className = '',
  decimalPlaces = 0,
}: NumberInputProps) {
  const [rawValue, setRawValue] = useState<string>(
    value !== undefined ? String(value) : ''
  );

  // Keep rawValue in sync when the parent updates value externally
  useEffect(() => {
    if (value === undefined) {
      setRawValue('');
    } else {
      // Only sync if the committed numeric value differs from what we show,
      // so we don't clobber a mid-edit string like "1."
      const parsed = parseFloat(rawValue);
      if (isNaN(parsed) || parsed !== value) {
        setRawValue(String(value));
      }
    }
  }, [value]); // eslint-disable-line react-hooks/exhaustive-deps

  const commit = (raw: string) => {
    const trimmed = raw.trim();
    if (trimmed === '' || trimmed === '-') {
      onChange(undefined);
      return;
    }
    const num = parseFloat(trimmed);
    if (isNaN(num)) {
      // Revert to last valid value
      setRawValue(value !== undefined ? String(value) : '');
      return;
    }
    const clamped = Math.min(max, Math.max(min, num));
    const rounded = parseFloat(clamped.toFixed(decimalPlaces));
    onChange(rounded);
    setRawValue(String(rounded));
  };

  const increment = () => {
    const current = value ?? min;
    const next = Math.min(max, parseFloat((current + step).toFixed(decimalPlaces)));
    onChange(next);
    setRawValue(String(next));
  };

  const decrement = () => {
    const current = value ?? min;
    const next = Math.max(min, parseFloat((current - step).toFixed(decimalPlaces)));
    onChange(next);
    setRawValue(String(next));
  };

  const atMin = value !== undefined && value <= min;
  const atMax = value !== undefined && value >= max;

  return (
    <div className={`flex items-stretch ${className}`}>
      {/* Decrement */}
      <button
        type="button"
        onClick={decrement}
        disabled={atMin}
        aria-label="Decrease"
        className="flex-none w-12 h-12 flex items-center justify-center rounded-l-lg border border-r-0 border-smoke-300 bg-smoke-100 text-smoke-700 hover:bg-smoke-200 active:bg-smoke-300 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
      >
        <Minus className="w-5 h-5" />
      </button>

      {/* Text field */}
      <input
        type="text"
        inputMode={decimalPlaces > 0 ? 'decimal' : 'numeric'}
        value={rawValue}
        placeholder={placeholder}
        onChange={(e) => setRawValue(e.target.value)}
        onBlur={(e) => commit(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') commit((e.target as HTMLInputElement).value);
        }}
        className="flex-1 min-w-0 px-3 py-3 h-12 text-center border border-smoke-300 focus:ring-2 focus:ring-flame-500 focus:border-flame-500 outline-none transition-colors text-lg font-medium"
      />

      {/* Increment */}
      <button
        type="button"
        onClick={increment}
        disabled={atMax}
        aria-label="Increase"
        className="flex-none w-12 h-12 flex items-center justify-center rounded-r-lg border border-l-0 border-smoke-300 bg-smoke-100 text-smoke-700 hover:bg-smoke-200 active:bg-smoke-300 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
      >
        <Plus className="w-5 h-5" />
      </button>
    </div>
  );
}
